/**
 * @module CallLog
 */

import { Duration, formatDuration, intervalToDuration } from "date-fns";
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { User } from "./user.model";
import { CallDirection } from "./call.model";

/**
 * Call type
 */
export enum CallLogType {
    /**
     * A call is considered "webrtc" when it initiated between 2 users using a Rainbow client (Web, Desktop or Mobile)
     * A "webrtc" call can be considered as an Internet call.
     */
    WEBRTC = "webrtc",

    /**
    * A call is considered "telephonic" when the party initiating the call has explicitly used a number (by dialing or selecting it) to reach the other party.
    */
    TELEPHONE = "telephone",

    /**
    * A call is considered a "conference" when it involves more than 2 participants.
    */
    CONFERENCE = "conference",
}

export interface CallLog {

    /**
     * The unique identifier of the call log
     * @readonly
     */
    id: string;

    /**
     * The associated user representing the other party (as caller or callee) of the call
     * @readonly
     */
    user: User;

    /**
     * The reading status, indicates if the call log has been marked as read or not.
     * @readonly
     */
    read: boolean;

    /**
     * The call state:
     * - "answered": Refers to an answered call with a conversation established and this, regardless of direction (Incoming or outgoing).
     * - "missed": Refers to an unanswered call, regardless of direction (Incoming or outgoing).
     * - "forwarded": Refers to an incoming call which has been forwarded to the connected user (who was not the initial destination).
     * @readonly
     */
    state: string;

    /**
     * The call type
     * @readonly
     */
    type: CallLogType;

    /**
     * The call direction
     * @readonly
     */
    direction: CallDirection;

    /**
     * The date of the call
     * @readonly
     */
    date: Date;

    /**
     * The call duration in milliseconds
     * @readonly
     */
    duration: number;

    /**
     * The subject of the call
     * @readonly
     */
    callSubject?: string;

    /**
     * The number of the called party
     * @readonly
     */
    calledNumber: string;

    /**
     * The number used to call back a Rainbow user if this one has not shared the private profile fields, including their telephone numbers.
     * As a result, there is no telephone number visible (so usable) in the user's associated contact object.
     * This number has been received as call information and is linked to the current call log only.
     * @readonly
     */
    userCallbackNumber?: string;
}

/**
 * @ignore
 */
export class CallLogRB implements CallLog {

    /**
     * The unique identifier of the call log
     * @readonly
     */
    public id: string;

    /**
     * The user related to the call log
     * @readonly
     */
    public user: Contact;

    /**
     * The reading status, indicates if the call log has been marked as read or not.
     * @readonly
     */
    public read: boolean;

    /**
     * The call state:
     * - "answered": Refers to an answered call with a conversation established and this, regardless of direction (Incoming or outgoing).
     * - "missed": Refers to an unanswered call, regardless of direction (Incoming or outgoing).
     * - "forwarded": Refers to an incoming call which has been forwarded to the connected user (who was not the initial destination).
     * @readonly
     */
    public state: string;

    /**
     * The call type
     * @readonly
     */
    public type: CallLogType;

    /**
     * The call direction
     * @readonly
     */
    public direction: CallDirection;

    /**
     * The date of the call
     * @readonly
     */
    public date: Date;

    /**
     * The call duration
     * @readonly
     */
    public duration: number;

    /**
     * The subject of the call
     * @readonly
     */
    public callSubject: string;

    /**
     * The number of the called party
     * @readonly
     */
    public calledNumber: string;

    /**
     * Call duration in HHMMSS format
     * @readonly
     */
    public durationInfos: string;

    /**
     * The number used to call back a Rainbow user if this one has not shared the private profile fields, including their telephone numbers.
     * As a result, there is no telephone number visible (so usable) in the user's associated contact object.
     * This number has been received as call information and is linked to the current call log only.
     * @readonly
     */
    public userCallbackNumber: string;

    // ----------------------------------------------------
    // UCaaS Only: UI management
    // ----------------------------------------------------

    /**
     * Icon used to indicate the direction of the call
     * @readonly
     */
    public svgId: string;

    /**
     * Class associated to the icon specified by 'svgId'
     * @readonly
     */
    public svgClass: string;

    /**
     * Title defined for the current call log according to its type and direction
     * @readonly
     */
    public title: string;

    // ----------------------------------------------------
    // UCaaS Only: Used for call aggregation management
    // ----------------------------------------------------

    /**
     * Used for call aggregation management
     * @readonly
     */
    public count: number = 0;

    /**
     * Used for call aggregation management
     * @readonly
     */
    public editable: boolean = false;

    /**
     * Used for call aggregation management
     * @readonly
     */
    public isMissed: boolean = false;

    /**
     * Used for call aggregation management
     * @readonly
     */
    public isNotAnswered: boolean = false;

    // ----------------------------------------------------
    // UCaaS Only: Internal management
    // ----------------------------------------------------

    /**
     * Id of the personal directory contact used for a tempoary name resolution applying only for old call logs for which
     * the server did not perform this operation.
     * @readonly
     */
    public directoryContactId: string;

    /**
     * Indicates if the call log must be ignored by Rainbow Web Client
     * @readonly
     */
    public isIgnoredByUCaaS: boolean;

    /**
     * When a name resolution is performed by the server, indicates the directory in which the correspondent's name was found.
     * @readonly
     */
    public identityDirectory: string;

    /**
     * When a name resolution is performed by the server, indicates the id of the directory contact.
     * The contact can come from one of the following directories: Personal directory, Business directory, Microsoft O365 contacts/AD users
     * @readonly
     */
    public identityContactId: string;

    /**
     * The "backup" user related to the call log
     * @readonly
     */
    public userOriginBackup: Contact;

    constructor(id: string, contact: Contact, state: string, duration: number, type: string, read: boolean, date: Date, direction: CallDirection, callSubject: string, ignoredByUCaaS: boolean, calledNumber: string, userCallbackNumber: string) {
        this.id = id;
        this.user = contact;
        this.state = state;
        this.duration = duration;
        this.read = read;
        this.date = date;
        this.direction = direction;
        this.callSubject = callSubject;
        this.isIgnoredByUCaaS = ignoredByUCaaS;
        this.calledNumber = calledNumber;
        this.userCallbackNumber = userCallbackNumber;

        if (type === "unknown" || type === "audio" || type === "webrtc") {
            this.type = CallLogType.WEBRTC;
        }
        else if (type === "telephone") {
            this.type = CallLogType.TELEPHONE;
        }
        else {
            this.type = CallLogType.CONFERENCE;
        }

        // Set Call duration in HHMMSS format
        if (this.duration > 0) {
            // Code using date-fns instead of moment
            const beginDuration = new Date(0);
            const endDuration = new Date(this.duration);

            const formatCallLogLocale = { xSeconds: '{{count}}s', xMinutes: '{{count}}m', xHours: '{{count}}H' };
            const callLogLocale = {
                formatDistance: (token, count) => {
                    const unitFormatted: string = formatCallLogLocale[token].replace('{{count}}', count);
                    // PMA: if necessary one day because the format doesn't fit
                    // if (token !== 'xHours') {
                    //     // If we want to To have 02s , 01m but to keep 2H
                    //     // Problem: "02m 15s" displayed instead of "2m 15s"
                    //     unitFormatted = unitFormatted.padStart(3, "0");
                    // }
                    return unitFormatted;
                }
            };

            const callDuration: Duration = intervalToDuration({ start: beginDuration, end: endDuration });
            this.durationInfos = formatDuration(callDuration, { format: ['hours', 'minutes', 'seconds'], locale: callLogLocale });
        } else {
            this.duration = 0;
            this.durationInfos = "";
        }

        // Determine if it's an unread missed call according to the specified rule
        this.isMissed = (!read && state === "missed" && direction === CallDirection.INCOMING);

        // By default, Personal Directory not used for finding the displayed name (name resolution is done as a last resort)
        this.directoryContactId = null;

        // Other default init of less useful data not given as parameter
        this.identityContactId = "";
        this.identityDirectory = "";
    }

    public static create(id: string, contact: Contact, state: string, duration: number, type: string, read: boolean, date: Date, direction: CallDirection, callSubject: string, ignoredByUCaaS: boolean, calledNumber: string = null, userCallbackNumber: string = null) {
        return new CallLogRB(id, contact, state, duration, type, read, date, direction, callSubject, ignoredByUCaaS, calledNumber, userCallbackNumber);
    }

    public static getDate(callLog: CallLogRB): number {
        if (callLog?.date) { return callLog.date.getTime(); }
        return 0;
    }

    public updateCallLogDisplayInfo(grouped: boolean = false) {
        if (this.direction === CallDirection.INCOMING) {
            // INCOMING CALL
            if (this.state === "missed") {
                this.svgId = "call_missed";
                this.title = (grouped && this.count > 1) ? "missedCalls" : "missedCall";
                this.svgClass = "missed";
            }
            else if (this.state === "forwarded") {
                this.svgId = "call_forwarded";
                this.title = "incomingCallForwarded";
                this.svgClass = "active";
            }
            else {
                // Answered Incoming Call
                this.svgId = "call_received";
                this.title = "incommingCall";
                this.svgClass = "active";
            }
        }
        else {
            // OUTGOING CALL
            // Reuse existing label that will be lowercased in the component (To avoid creation of a new one)
            this.title = (grouped && this.count > 1) ? "outgoingCalls" : "outgoingCall";
            if (this.state === "missed") {
                // Not answered outgoing call (RAC of the Caller)
                this.svgId = "call_canceled";
                this.svgClass = "cancel";
            }
            else {
                this.svgId = "call_made";
                this.svgClass = "active";
            }
        }
    }
}
